<template>
  <li :class="{ 'lc-node-flat': flat }">
    <Modal
      :width="400"
      :value="inputModalOpen"
      ctaText="Add"
      actions-border
      overlayColor="#ffffff"
      overlayOpacity="0.83"
      @cancel="inputModalOpen = false"
      title="Add input question"
      @confirm="confirmInputQuestionModal">
      <template #default>
        <label class="default-question__label"> {{ $t("shortcodes.live_chat_auto.default_question") }} </label>
        <v-select
          v-model="inputQuestionValue"
          class="input-question__select"
          hide-details
          :items="inputQuestionOptions"
          :menu-props="{
            offsetOverflow: true,
            offsetY: true,
          }"
          placeholder="Input question">
        </v-select>
      </template>
    </Modal>

    <span
      class="tree-item"
      :data-node-id="node.id"
      :class="{
        'tree-item--last-message': isLastMessageInBranch,
        'tree-item--error': node._telHrefError,
      }">
      <LiveChatLinkPorts :node="node" />
      <div class="sender-indicator">
        <div class="d-flex align-center">
          <v-tooltip v-if="node.messageType === 'decision'" top>
            <template #activator="{ on }">
              <div v-on="on">
                <v-icon class="sender-indicator--forum mr-2">mdi-eye-off-outline</v-icon>
              </div>
            </template>
            <span>{{ $t("shortcodes.live_chat_auto.decision_tooltip") }}</span>
          </v-tooltip>
          <v-icon v-else class="sender-indicator--bot mr-2">mdi-robot</v-icon>

          <span v-if="node.messageType === 'message'">
            {{ $t("shortcodes.live_chat_auto.message") }}
            <v-icon v-if="isLastMessageInBranch" class="ml-1" color="green" size="16">mdi-message-arrow-right</v-icon>
          </span>
          <span v-if="node.messageType === 'inputQuestion'">{{ $t("shortcodes.live_chat_auto.input_question") }}</span>
          <span v-if="node.messageType === 'zipInputQuestion'">{{
            $t("shortcodes.live_chat_auto.zip_input_question")
          }}</span>
          <span v-if="node.messageType === 'decision'">{{ $t("shortcodes.live_chat_auto.decision") }}</span>
          <span v-if="node.messageType === 'action'">
            {{ $t("shortcodes.live_chat_auto.action") }}
          </span>
          <span v-if="node.messageType === 'actionItem'">
            {{ $t("shortcodes.live_chat_auto.action_item") }}
            <span>{{ index + 1 }}</span>
          </span>
        </div>
      </div>
      <div v-if="parent" @click="removeNode" class="remove-node">
        <v-icon>mdi-trash-can-outline</v-icon>
      </div>
      <div
        v-if="canDeleteBranch"
        @click="removeBranch"
        class="delete-branch"
        :class="{ 'delete-branch--offset': canCopyNode }">
        <v-icon>mdi-delete-sweep-outline</v-icon>
      </div>
      <div v-if="canCopyNode" @click="copyNode" class="copy-node">
        <v-icon>mdi-dock-window</v-icon>
      </div>
      <div>
        <div class="message-item">
          <LiveChatMp4Message
            ref="message"
            v-if="node.messageType === 'message'"
            :node.sync="node"></LiveChatMp4Message>
          <LiveChatMp4InputQuestion
            ref="inputQuestion"
            v-if="node.messageType === 'inputQuestion'"
            :node.sync="node"></LiveChatMp4InputQuestion>
          <LiveChatMp4ZipInput
            ref="zipInput"
            v-if="node.messageType === 'zipInputQuestion'"
            :node.sync="node"></LiveChatMp4ZipInput>
          <LiveChatMp4Action v-if="node.messageType === 'action'" :node.sync="node"></LiveChatMp4Action>
          <LiveChatMp4ActionItem v-if="node.messageType === 'actionItem'" :node.sync="node"></LiveChatMp4ActionItem>
          <LiveChatMp4Decision
            v-if="node.messageType === 'decision' && (!parent || parent.messageType !== 'zipInputQuestion')"
            :node.sync="node"></LiveChatMp4Decision>
          <LiveChatMp4ZipDecision
            v-if="node.messageType === 'decision' && parent && parent.messageType === 'zipInputQuestion'"
            :node.sync="node"></LiveChatMp4ZipDecision>
        </div>
        <div class="lc-node-btns">
          <v-btn class="mr-2 lc-node-btn" v-if="displayAddMessageBtn" @click="addMessage">{{
            $t("shortcodes.live_chat_auto.add_message")
          }}</v-btn>
          <v-btn class="mr-2 lc-node-btn" v-if="displayAddActionBtn" @click="addAction">{{
            $t("shortcodes.live_chat_auto.add_action")
          }}</v-btn>
          <v-btn class="mr-2 lc-node-btn" v-if="displayAddActionItem" @click="addActionItem">{{
            $t("shortcodes.live_chat_auto.add_action_item")
          }}</v-btn>
          <v-btn class="mr-2 lc-node-btn" v-if="displayAddInputQuestion" @click="openInputQuestionModal">{{
            $t("shortcodes.live_chat_auto.add_input_question")
          }}</v-btn>
          <v-btn class="mr-2 lc-node-btn" v-if="displayAddDecision" @click="addDecision">{{
            $t("shortcodes.live_chat_auto.add_decision")
          }}</v-btn>
        </div>
      </div>
    </span>
    <ul v-if="!flat && node.children && node.children.length">
      <LiveChatMp4V2TreeNode
        v-for="(child, index) in node.children"
        ref="children"
        :index="index"
        :key="child.id"
        :node="child"
        :parent="node"
        :lastMessageMap="lastMessageMap"
        @openModal="$emit('openModal', $event)"
        @recomputeLastMessageMap="$emit('recomputeLastMessageMap')"
        @pruneDanglingNext="$emit('pruneDanglingNext')"></LiveChatMp4V2TreeNode>
    </ul>
  </li>
</template>

<script>
  import cloneDeep from "lodash/cloneDeep";
  import LiveChatMp4Message from "../livechatMp4/LiveChatMp4Message.vue";
  import LiveChatMp4InputQuestion from "../livechatMp4/LiveChatMp4InputQuestion.vue";
  import LiveChatMp4ZipInput from "../livechatMp4/LiveChatMp4ZipInput.vue";
  import LiveChatMp4Decision from "../livechatMp4/LiveChatMp4Decision.vue";
  import LiveChatMp4ZipDecision from "../livechatMp4/LiveChatMp4ZipDecision.vue";
  import LiveChatMp4Action from "../livechatMp4/LiveChatMp4Action.vue";
  import LiveChatMp4ActionItem from "../livechatMp4/LiveChatMp4ActionItem.vue";
  import LiveChatLinkPorts from "../livechatV2/LiveChatLinkPorts.vue";
  import Modal from "@/components/custom/Modal.vue";
  import {
    MESSAGE_MP4,
    ACTION,
    ACTION_ITEM,
    INPUT_QUESTION,
    ZIP_INPUT_QUESTION,
    DECISION,
    INPUT_QUESTION_DISPLAY_TYPES,
    ZIP_INPUT_DISPLAY_TYPES,
    LIVE_CHAT_MP4_ACTION_DEFAULTS,
    LIVE_CHAT_MP4_ACTION_ITEM_DEFAULTS,
  } from "./constant";
  import { v4 as uuidv4 } from "uuid";
  import { normalizeIds } from "../livechatV2/treeGraph";

  export default {
    name: "LiveChatMp4V2TreeNode",
    components: {
      LiveChatMp4Message,
      LiveChatMp4InputQuestion,
      LiveChatMp4ZipInput,
      LiveChatMp4Decision,
      LiveChatMp4ZipDecision,
      LiveChatMp4Action,
      LiveChatMp4ActionItem,
      LiveChatLinkPorts,
      Modal,
    },
    props: {
      node: {
        type: Object,
        required: true,
      },
      parent: {
        type: Object,
        default: null,
      },
      index: {
        type: Number,
        default: 0,
      },
      lastMessageMap: {
        type: Object,
        default: () => ({}),
      },
      flat: {
        type: Boolean,
        default: false,
      },
    },
    emits: ["openModal", "recomputeLastMessageMap", "pruneDanglingNext"],
    data() {
      return {
        inputModalOpen: false,
        inputQuestionValue: "enter_age",
        inputQuestionOptions: [
          { text: "Enter your age", value: "enter_age" },
          { text: "Enter your birth year", value: "enter_birth_year" },
          { text: "Enter your birth year (Underscore)", value: "enter_birth_year_underscore" },
          { text: "Enter ZIP code", value: "enter_zip" },
        ],
      };
    },
    computed: {
      canCopyNode() {
        if (this.node.messageType === "actionItem") return true;
        // ZIP input decisions are fixed branches, so there is no duplicate action there.
        if (this.node.messageType === "decision") {
          return !(this.parent && this.parent.messageType === "zipInputQuestion");
        }

        return false;
      },
      canDeleteBranch() {
        return !!this.parent && Array.isArray(this.node.children) && this.node.children.length > 0;
      },
      displayAddMessageBtn() {
        if (this.node.messageType === "action") return false;
        if (this.node.messageType === "inputQuestion") return false;
        if (this.node.messageType === "zipInputQuestion") return false;

        return true;
      },
      displayAddActionBtn() {
        if (this.node.children?.some(childNode => childNode.messageType === "action")) return false;
        if (this.node.children?.some(childNode => childNode.messageType === "inputQuestion")) return false;
        if (this.node.children?.some(childNode => childNode.messageType === "zipInputQuestion")) return false;
        if (this.node.messageType === "actionItem") return false;
        if (this.node.messageType === "action") return false;
        if (this.node.messageType === "inputQuestion") return false;
        if (this.node.messageType === "zipInputQuestion") return false;

        return true;
      },
      displayAddActionItem() {
        return this.node.messageType === "action";
      },
      displayAddInputQuestion() {
        if (this.node.messageType === "action") return false;
        if (this.node.messageType === "inputQuestion") return false;
        if (this.node.messageType === "zipInputQuestion") return false;
        if (
          this.node.children?.some(
            childNode =>
              childNode.messageType === "inputQuestion" ||
              childNode.messageType === "zipInputQuestion" ||
              childNode.messageType === "action",
          )
        )
          return false;

        return true;
      },
      displayAddDecision() {
        if (this.node.messageType === "inputQuestion") return true;
        if (this.node.messageType === "zipInputQuestion") {
          return !this.node.children?.some(childNode => childNode.messageType === "decision");
        }

        return false;
      },
      isLastMessageInBranch() {
        if (this.node.messageType !== "message") {
          if (this.node.isLastMessage !== false) {
            this.$set(this.node, "isLastMessage", false);
          }

          return false;
        }

        if (this.node.next) {
          if (this.node.isLastMessage !== false) {
            this.$set(this.node, "isLastMessage", false);
          }

          return false;
        }

        let isLast = false;
        if (this.node.id && this.lastMessageMap && Object.keys(this.lastMessageMap).length > 0) {
          isLast = !!this.lastMessageMap[this.node.id];
        } else {
          isLast = !this.hasDescendantMessage(this.node);
        }

        // Only write on change, so the reactive update cannot disturb the open message editor.
        if (this.node.isLastMessage !== isLast) {
          this.$set(this.node, "isLastMessage", isLast);
        }

        return isLast;
      },
    },
    watch: {
      "node.content"() {
        if (this.node._telHrefError) this.$delete(this.node, "_telHrefError");
      },
      "node.text"() {
        if (this.node._telHrefError) this.$delete(this.node, "_telHrefError");
      },
      "node.value"() {
        if (this.node._telHrefError) this.$delete(this.node, "_telHrefError");
      },
    },
    beforeMount() {
      this.ensureIds(this.node);
      this.ensureChildrenVariableExists(this.node);
    },
    methods: {
      ensureIds(node) {
        normalizeIds([node]);
      },
      ensureChildrenVariableExists(node) {
        if (!node.children) {
          this.$set(node, "children", []);
        }
      },
      // A node has children OR `next`, never both.
      clearNext() {
        if (this.node.next) {
          this.$set(this.node, "next", null);
        }
      },
      hasDescendantMessage(node) {
        if (!node || !Array.isArray(node.children) || node.children.length === 0) return false;
        for (let i = 0; i < node.children.length; i += 1) {
          const child = node.children[i];
          if (child.messageType === "message") return true;
          if (this.hasDescendantMessage(child)) return true;
        }

        return false;
      },
      copyNode() {
        const copiedNode = cloneDeep(this.node);
        // A `next` pointing inside the copied subtree must be remapped to the new ids, else it links back to the original.
        const idMap = {};
        const regenerateIds = node => {
          if (!node || typeof node !== "object") return;
          const newId = uuidv4();
          if (node.id) idMap[node.id] = newId;
          node.id = newId;
          if (Array.isArray(node.children)) node.children.forEach(regenerateIds);
        };
        const remapNext = node => {
          if (!node || typeof node !== "object") return;
          if (node.next && idMap[node.next]) node.next = idMap[node.next];
          if (Array.isArray(node.children)) node.children.forEach(remapNext);
        };
        regenerateIds(copiedNode);
        remapNext(copiedNode);
        let children = this.parent.children;
        this.parent.children = [];
        this.$nextTick(() => {
          children.splice(this.index + 1, 0, copiedNode);
          this.parent.children = children;
        });
      },
      removeNode() {
        if (this.node.messageType === "actionItem" && this.node.children.length > 0) {
          this.$emit("openModal", {
            title: "Warning!",
            subtitle:
              "If an action item is deleted, all messages and other action items coming after it will also be deleted.",
            ctaText: "Delete",
            delete: true,
            index: this.index,
            parent: this.parent,
          });
        } else if (this.node.messageType === "decision" && this.node.children.length > 0) {
          this.$emit("openModal", {
            title: "Warning!",
            subtitle: "If a decision is deleted, all messages and other decision coming after it will also be deleted.",
            ctaText: "Delete",
            delete: true,
            index: this.index,
            parent: this.parent,
          });
        } else if (this.node.messageType === "action" && this.node.children.length > 0) {
          this.$emit("openModal", {
            title: "Cannot delete!",
            subtitle:
              '"Action" cannot be deleted, because it still has action items that belong to it. Action items will not function without "Action".',
            ctaText: "Ok, understood",
            delete: false,
            cancelHidden: true,
          });
        } else if (this.node.messageType === "inputQuestion" && this.node.children.length > 0) {
          this.$emit("openModal", {
            title: "Cannot delete!",
            subtitle:
              '"Input question" cannot be deleted, because it still has decisions that belong to it. Decisions will not function without "Input question".',
            ctaText: "Ok, understood",
            delete: false,
            cancelHidden: true,
          });
        } else if (this.node.messageType === "zipInputQuestion" && this.node.children.length > 0) {
          this.$emit("openModal", {
            title: "Cannot delete!",
            subtitle:
              '"ZIP input question" cannot be deleted, because it still has decisions that belong to it. Decisions will not function without "ZIP input question".',
            ctaText: "Ok, understood",
            delete: false,
            cancelHidden: true,
          });
        } else if (this.node.children.length > 0) {
          const children = this.node.children;
          this.updateTreeContent(children, this);
          this.parent.children = children;
          this.$emit("pruneDanglingNext");
        } else {
          this.parent.children.splice(this.parent.children.indexOf(this.node), 1);
          this.$emit("pruneDanglingNext");
        }
      },
      // Deletes the node and its whole subtree (removeNode keeps children).
      removeBranch() {
        this.$emit("openModal", {
          title: "Delete branch?",
          subtitle:
            "This node and all its children (every message, action, and decision below it) will be deleted. This action cannot be undone.",
          ctaText: "Delete",
          delete: true,
          index: this.index,
          parent: this.parent,
        });
      },
      openInputQuestionModal() {
        this.inputModalOpen = true;
      },
      confirmInputQuestionModal() {
        this.inputModalOpen = false;
        this.addInputQuestion();
      },
      addAction() {
        this.clearNext();
        const action = cloneDeep({ ...ACTION, ...LIVE_CHAT_MP4_ACTION_DEFAULTS });
        this.ensureIds(action);
        this.ensureChildrenVariableExists(this.node);

        if (this.node.children.length > 0) {
          const actionItem = cloneDeep({ ...ACTION_ITEM, ...LIVE_CHAT_MP4_ACTION_ITEM_DEFAULTS });
          this.ensureIds(actionItem);
          const children = this.node.children;
          action.children = [actionItem];
          actionItem.children = children;
          this.node.children = [action];
        } else {
          this.node.children.push(action);
        }
      },
      addActionItem() {
        this.clearNext();
        const actionItem = cloneDeep({ ...ACTION_ITEM, ...LIVE_CHAT_MP4_ACTION_ITEM_DEFAULTS });
        this.ensureIds(actionItem);
        this.ensureChildrenVariableExists(this.node);

        this.node.children.push(actionItem);
      },
      addInputQuestion() {
        if (this.inputQuestionValue === "enter_zip") {
          this.addZipInputQuestion();

          return;
        }

        this.clearNext();
        const inputQuestion = cloneDeep(INPUT_QUESTION);
        inputQuestion.inputQuestionType = this.inputQuestionValue;
        inputQuestion.inputQuestionDisplayType =
          this.inputQuestionValue === "enter_age"
            ? INPUT_QUESTION_DISPLAY_TYPES.find(type => type.value === "age").value
            : INPUT_QUESTION_DISPLAY_TYPES.find(type => type.value === "years").value;

        this.ensureIds(inputQuestion);
        this.node.children.push(inputQuestion);
      },
      addZipInputQuestion() {
        this.clearNext();
        const zipInputQuestion = cloneDeep(ZIP_INPUT_QUESTION);
        zipInputQuestion.zipInputDisplayType = ZIP_INPUT_DISPLAY_TYPES.find(type => type.value === "empty").value;
        this.ensureIds(zipInputQuestion);
        this.ensureChildrenVariableExists(this.node);

        this.node.children.push(zipInputQuestion);
      },
      addDecision() {
        this.clearNext();
        const decision = cloneDeep(DECISION);
        this.ensureIds(decision);
        this.node.children.push(decision);
      },
      addMessage() {
        this.clearNext();
        const message = cloneDeep(MESSAGE_MP4);
        this.ensureIds(message);
        this.ensureChildrenVariableExists(this.node);

        if (this.node.children.length > 0) {
          const children = this.node.children;
          message.children = children;
          // In flat mode there are no child refs, so updateTreeContent is a no-op.
          this.updateTreeContent([message], this.$refs.children && this.$refs.children[0]);
          this.node.children = [message];
        } else {
          this.node.children.push(message);
        }

        this.$nextTick(() => {
          this.$emit("recomputeLastMessageMap");
        });
      },
      updateTreeContent(children, ref) {
        let index = ref?.index ? ref.index : 0;
        if (children[index].messageType === "message" && ref && ref.$refs.message) {
          ref.$refs.message.updateContent(children[index].content);
          ref.$refs.message.showEditor = !children[index].content;
          if (children[index].children?.length > 0 && ref.$refs.children) {
            this.updateTreeContent(children[index].children, ref.$refs.children[index]);
          }
        }
      },
    },
  };
</script>

<style lang="scss" scoped>
  @import "@core/preset/preset/typography/index.scss";
  @import "@core/preset/preset/functions.scss";
  .sender-indicator {
    position: absolute;
    top: 10px;
    left: 10px;
    &--bot {
      color: orange !important;
    }
    &--forum {
      color: #2377f8 !important;
    }
  }
  // Graph mode: the node is outside `.tree`, so restore what the `.tree span.tree-item` rule provided.
  .lc-node-flat {
    display: block;
    list-style: none;
    margin: 0;
    padding: 0;

    .tree-item {
      display: inline-block;
      // relative so the absolute ports/icons anchor to the box, not the outer .lc-gnode
      position: relative;
      min-width: 600px;
      max-width: 600px;
      border: solid 0.1em #666;
      border-radius: 0.2em;
    }
  }
  .tree-item {
    padding: 45px 10px 10px 10px;
    border-radius: 5px;
    &--error {
      border: 3px dashed get-color("error") !important;
    }
  }
  .lc-node-btns {
    text-align: center;
  }

  .lc-node-btn.v-btn:not(.v-btn--round).v-size--default {
    height: 44px;
    padding: 0 24px;
    // clears the bottom-center link port
    margin-bottom: 24px;
  }

  .remove-node {
    padding: 5px;
    position: absolute;
    top: 0;
    right: 0;
    cursor: pointer;
    z-index: 2;
  }

  .copy-node {
    padding: 5px 35px 5px 5px;
    position: absolute;
    top: 0;
    right: 0;
    cursor: pointer;
    z-index: 1;
  }

  .delete-branch {
    padding: 5px 35px 5px 5px;
    position: absolute;
    top: 0;
    right: 0;
    cursor: pointer;
    z-index: 1;

    &--offset {
      padding-right: 65px;
    }
  }

  .remove-node:hover .v-icon,
  .delete-branch:hover .v-icon {
    color: get-color("error") !important;
  }

  .copy-node:hover .v-icon {
    color: get-color("primary") !important;
  }

  .default-question {
    &__label {
      color: get-color("subtitle");
      @include text-caption-bold();
    }
  }
</style>
